import React, { FC, useCallback } from 'react';
import { TenderStatusBadge, StarToggler, Tooltip } from 'src/common';
import styles from './index.module.scss';
import { AssignToTender, TenderBox, TenderReportBadge } from '../Tender';
import LinkToBuyer from '../LinkToBuyer';
import { isFieldCurrency, isFieldDate, isFieldString, TenderBoxMode } from 'src/models/procurements/Tender/types';
import { useUpdateIsStarredTender } from 'src/models/procurements/hooks';
import InformationFields from 'src/shared/Tender/InformationFields';
import { useTenderNav } from 'src/models/procurements/TenderNav/hooks';
import { AssignToBid, BidQuickActions, ByggfaktaProjectButton } from '../Bid';
import { BidItemType } from '@tendium/prom-types/tender';

export const TenderHeader: FC = () => {
  const { data } = useTenderNav();
  const [updateTenderIsStarred] = useUpdateIsStarredTender();
  const isEditable = !!data?.info.isEditable;

  const onSetStarred = useCallback(
    (id: string, name: string, starred: boolean): void => {
      updateTenderIsStarred(id, name, starred, 'TenderMainPage');
    },
    [updateTenderIsStarred]
  );

  const nameBox = data?.info.name;
  const name = nameBox && isFieldString(nameBox.firstField) ? nameBox.firstField.string : '';
  const estValue =
    data?.info.estimatedValue && isFieldCurrency(data.info.estimatedValue.firstField)
      ? data.info.estimatedValue.firstField
      : null;
  const published =
    data?.info.published && isFieldDate(data.info.published.firstField) && !!data.info.published.firstField.date
      ? data.info.published.firstField.date
      : null;
  const deadline =
    data?.info.deadline && isFieldDate(data.info.deadline.firstField) && !!data.info.deadline.firstField.date
      ? data.info.deadline.firstField.date
      : null;

  return data ? (
    <div className={styles.header}>
      <div className={styles.title}>
        <Tooltip title={name}>
          {isEditable ? (
            <TenderBox
              actions={false}
              box={nameBox}
              title={false}
              className={styles.titleBox}
              as={TenderBoxMode.Custom}
              isEditable
            />
          ) : (
            <h1 className={styles.titleValue}>{name}</h1>
          )}
        </Tooltip>
        {data.bid?.wsId && !data?.isBidding && (
          <BidQuickActions
            bidId={data.bid?.id}
            wsId={data.bid?.wsId}
            originType={BidItemType.Procurement}
            originId={data.id}
          />
        )}
      </div>
      <div className={styles.bar}>
        <LinkToBuyer buyerBox={data.info.buyer} linkBox={data.info.linkToBuyer} className={styles.buyerLink} />
        <div className={styles.actions}>
          <ByggfaktaProjectButton bidId={data?.bid?.id} isActive={data?.isBfProject} />
          <StarToggler onSetStarred={onSetStarred} isStarred={data.info.isStarred} id={data.id} name={name || ''} />
          <TenderStatusBadge statuses={data.info.procurementStatus} />
          {(data.info.isFilledData || data.info.isInProgress) && (
            <TenderReportBadge isFilledData={data.info.isFilledData} isInProgress={data.info.isInProgress} />
          )}
          {data.isBidding && data.bid ? (
            <AssignToBid
              bidId={data.bid.id}
              wsId={data.bid.wsId}
              assignedTo={data.info.assignedTo}
              eventSource={'BiddingTool'}
            />
          ) : (
            <AssignToTender assignedTo={data.info.assignedTo} procId={data.id} eventSource={'TenderMainPage'} />
          )}
          <InformationFields estimationValue={estValue} deadline={deadline} published={published} shortMode />
        </div>
      </div>
    </div>
  ) : null;
};

export default TenderHeader;
